import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import api from "../../services/api";
import classService from "../../services/classService";
import PromotionHistoryLogPanel from "../../components/student/PromotionHistoryLogPanel";

const ENROLLMENT_STATUSES = [
  { value: "ACTIVE", label: "Active (Promoted)" },
  { value: "DETAINED", label: "Detained" },
  { value: "REPEATER", label: "Repeater" },
  { value: "GRADUATED", label: "Graduated" },
  { value: "TRANSFERRED", label: "Transfer (TC)" },
  { value: "DROPPED", label: "Dropout" },
];

const PROMOTION_TYPES = [
  { value: "PROMOTION", label: "Promotion" },
  { value: "CLASS_CHANGE", label: "Class change" },
  { value: "SECTION_CHANGE", label: "Section change" },
  { value: "SESSION_CHANGE", label: "Session change" },
  { value: "STATUS_CHANGE", label: "Status change" },
];

export default function StudentPromotion() {
  const [loading, setLoading] = useState({ classes: false, students: false, promote: false });
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

  const [classes, setClasses] = useState([]);
  const [fromClassId, setFromClassId] = useState("");
  const [students, setStudents] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [promotionHistory, setPromotionHistory] = useState([]);
  const [toClassId, setToClassId] = useState("");
  const [enrollmentStatus, setEnrollmentStatus] = useState("ACTIVE");
  const [promotionType, setPromotionType] = useState("PROMOTION");
  const [reason, setReason] = useState("");
  const [practicalBatchCode, setPracticalBatchCode] = useState("");
  const [promotionLogRefresh, setPromotionLogRefresh] = useState(0);

  const classOptions = useMemo(() => {
    return (Array.isArray(classes) ? classes : []).map((c) => ({
      id: c.id,
      name: c.name,
      section: c.section,
      displayName: c.section ? `${c.name} - ${c.section}` : c.name,
    }));
  }, [classes]);

  useEffect(() => {
    let mounted = true;
    const loadClasses = async () => {
      setLoading((p) => ({ ...p, classes: true }));
      try {
        const resp = await classService.getAllClasses({ page: 1, limit: 200, sort: "id", order: "DESC" });
        const items =
          resp?.data?.classes ??
          resp?.classes ??
          resp?.data?.data ??
          resp?.data ??
          [];
        if (!mounted) return;
        setClasses(Array.isArray(items) ? items : []);
      } catch (e) {
        if (!mounted) return;
        setSnackbar({ open: true, severity: "error", message: e?.response?.data?.message || "Failed to load classes" });
      } finally {
        if (mounted) setLoading((p) => ({ ...p, classes: false }));
      }
    };
    loadClasses();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadStudents = async () => {
      if (!fromClassId) {
        setStudents([]);
        return;
      }
      setLoading((p) => ({ ...p, students: true }));
      try {
        const resp = await api.get("/student/get-all-students", {
          params: { page: 1, limit: 200, classId: fromClassId },
        });
        const items = resp?.data?.data?.students ?? resp?.data?.data?.rows ?? resp?.data?.data ?? [];
        if (!mounted) return;
        setStudents(Array.isArray(items) ? items : []);
      } catch (e) {
        if (!mounted) return;
        setSnackbar({ open: true, severity: "error", message: e?.response?.data?.message || "Failed to load students" });
      } finally {
        if (mounted) setLoading((p) => ({ ...p, students: false }));
      }
    };
    loadStudents();
    return () => {
      mounted = false;
    };
  }, [fromClassId]);

  const rows = useMemo(() => {
    return (Array.isArray(students) ? students : []).map((s) => ({
      id: s.id,
      rollNumber: s.rollNumber || s.rollNo || "",
      studentLoginID: s.studentLoginID ?? s.studentId ?? s.rollNo ?? "",
      name: s?.User?.fullName || s?.user?.fullName || s?.User?.email || s?.user?.email || "",
      email: s?.User?.email || s?.user?.email || "",
      status: s?.studentStatus || "",
      raw: s,
    }));
  }, [students]);

  const columns = [
    { field: "rollNumber", headerName: "Roll No.", width: 110 },
    { field: "studentLoginID", headerName: "Login ID", width: 140 },
    { field: "name", headerName: "Student", flex: 1, minWidth: 220 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 220 },
    { field: "status", headerName: "Status", width: 140 },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            setSelectedStudent(params.row.raw);
            setToClassId("");
            setEnrollmentStatus("ACTIVE");
            setPromotionType("PROMOTION");
            setReason("");
            setPracticalBatchCode("");
            setDialogOpen(true);
          }}
        >
          Promote
        </Button>
      ),
    },
  ];

  const handlePromote = async () => {
    if (!selectedStudent?.id) return;
    if (!toClassId) {
      setSnackbar({ open: true, severity: "error", message: "Select a target class" });
      return;
    }
    setLoading((p) => ({ ...p, promote: true }));
    try {
      await api.post("/student-promotion/promote", {
        studentId: selectedStudent.id,
        toClassId: Number(toClassId),
        enrollmentStatus,
        type: promotionType,
        reason: reason || null,
        practicalBatchCode: practicalBatchCode || null,
        backlogSubjectIds: [],
      });
      setSnackbar({ open: true, severity: "success", message: "Student promoted successfully" });
      setPromotionLogRefresh((x) => x + 1);
      setDialogOpen(false);
      // Refresh list so the student disappears from old class roster if class changed
      const resp = await api.get("/student/get-all-students", { params: { page: 1, limit: 200, classId: fromClassId } });
      const items = resp?.data?.data?.students ?? [];
      setStudents(Array.isArray(items) ? items : []);
    } catch (e) {
      setSnackbar({ open: true, severity: "error", message: e?.response?.data?.message || "Promotion failed" });
    } finally {
      setLoading((p) => ({ ...p, promote: false }));
    }
  };

  useEffect(() => {
    let mounted = true;
    const loadHistory = async () => {
      if (!dialogOpen || !selectedStudent?.id) return;
      setHistoryLoading(true);
      try {
        const resp = await api.get(`/student-promotion/history/${selectedStudent.id}`, { params: { limit: 10, offset: 0 } });
        const rows = resp?.data?.data?.rows ?? [];
        if (!mounted) return;
        setPromotionHistory(Array.isArray(rows) ? rows : []);
      } catch (_e) {
        if (!mounted) return;
        setPromotionHistory([]);
      } finally {
        if (mounted) setHistoryLoading(false);
      }
    };
    loadHistory();
    return () => {
      mounted = false;
    };
  }, [dialogOpen, selectedStudent?.id]);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Student Promotion
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Promote students safely while keeping history intact.
          </Typography>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
            Promotion & placement history
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Records are written automatically on each promotion (who, when, from class, to class, and a JSON snapshot of
            placement before/after). Use filters to narrow by class or date.
          </Typography>
          <PromotionHistoryLogPanel classOptions={classOptions} refreshKey={promotionLogRefresh} />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" disabled={loading.classes}>
                <InputLabel id="from-class-label">From class</InputLabel>
                <Select
                  labelId="from-class-label"
                  value={fromClassId}
                  label="From class"
                  onChange={(e) => setFromClassId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select class</em>
                  </MenuItem>
                  {classOptions.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              autoHeight
              disableRowSelectionOnClick
              loading={loading.students}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setDialogOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Promote student</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Student:{" "}
            <strong>
              {selectedStudent?.User?.fullName ||
                selectedStudent?.user?.fullName ||
                selectedStudent?.User?.email ||
                selectedStudent?.user?.email ||
                selectedStudent?.studentLoginID ||
                selectedStudent?.id}
            </strong>
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="to-class-label">To class</InputLabel>
                <Select labelId="to-class-label" value={toClassId} label="To class" onChange={(e) => setToClassId(e.target.value)}>
                  <MenuItem value="">
                    <em>Select target class</em>
                  </MenuItem>
                  {classOptions.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="promotion-type-label">Type</InputLabel>
                <Select
                  labelId="promotion-type-label"
                  value={promotionType}
                  label="Type"
                  onChange={(e) => setPromotionType(e.target.value)}
                >
                  {PROMOTION_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="enrollment-status-label">Enrollment status</InputLabel>
                <Select
                  labelId="enrollment-status-label"
                  value={enrollmentStatus}
                  label="Enrollment status"
                  onChange={(e) => setEnrollmentStatus(e.target.value)}
                >
                  {ENROLLMENT_STATUSES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Practical batch (optional)"
                value={practicalBatchCode}
                onChange={(e) => setPracticalBatchCode(e.target.value)}
                fullWidth
                size="small"
                placeholder="A1"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Recent promotion history
            </Typography>
            {historyLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading history…
                </Typography>
              </Box>
            ) : promotionHistory.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No promotion history found.
              </Typography>
            ) : (
              <Box sx={{ display: "grid", gap: 0.75 }}>
                {promotionHistory.map((h) => (
                  <Box key={h.id} sx={{ p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {h.type} • {h.status}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {h.promotedAt ? new Date(h.promotedAt).toLocaleString() : ""}
                      {h.reason ? ` • ${h.reason}` : ""}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit" disabled={loading.promote}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handlePromote} disabled={loading.promote}>
            {loading.promote ? <CircularProgress size={20} /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

